import { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from "react-hot-toast";
import moment from 'moment';
import { Link } from 'react-router-dom';
import TaskSkeleton from './TaskSkeleton';

export default function StreamVideoList() {
    const { http, user } = AuthUser();
    const [videos, setVideos] = useState([]);
    const [categories, setCategories] = useState([]);
    const [videoCategories, setVideoCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingVideo, setEditingVideo] = useState(null);
    const [formData, setFormData] = useState({
        category_id: '',
        desc: '',
        video_link: '',
        paid: 0,
        etc_2: '',
        selectedVideoCategories: [],
        thumb: null
    });

    useEffect(() => {
        fetchVideos();
        fetchCategories();
        fetchVideoCategories();
    }, []);

    const fetchVideos = () => {
        setIsLoading(true);
        http.get('/stream_videos').then((res) => {
            setIsLoading(false);
            if (res.data.success && Array.isArray(res.data.data)) {
                setVideos(res.data.data);
            } else {
                console.error("API response error:", res.data);
                toast.error("Failed to fetch videos");
            }
        }).catch(error => {
            setIsLoading(false);
            console.error("Error fetching videos:", error);
            toast.error("Error fetching videos");
        });
    };

    const fetchCategories = () => {
        http.get('/stream_categories').then((res) => {
            if (res.data.success && Array.isArray(res.data.data)) {
                setCategories(res.data.data);
            }
        }).catch(error => {
            console.error("Error fetching categories:", error);
        });
    };

    const fetchVideoCategories = () => {
        http.get('/stream_vidcategories').then((res) => {
            if (res.data.success && Array.isArray(res.data.data)) {
                setVideoCategories(res.data.data);
            }
        }).catch(error => {
            console.error("Error fetching video categories:", error);
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'paid' ? parseInt(value, 10) : value
        }));
    };

    const handleFileChange = (e) => {
        setFormData(prev => ({
            ...prev,
            thumb: e.target.files[0]
        }));
    };

    const handleVideoCategoryChange = (categoryId) => {
        setFormData(prev => {
            const isSelected = prev.selectedVideoCategories.includes(categoryId);
            if (isSelected) {
                return {
                    ...prev,
                    selectedVideoCategories: prev.selectedVideoCategories.filter(id => id !== categoryId)
                };
            } else {
                return {
                    ...prev,
                    selectedVideoCategories: [...prev.selectedVideoCategories, categoryId]
                };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const submitData = new FormData();
        submitData.append('category_id', formData.category_id || '');
        submitData.append('desc', formData.desc || '');
        submitData.append('video_link', formData.video_link || '');
        
        // Always send paid - send 0 or 1 (0 = No, 1 = Yes)
        // Ensure we always send a value (0 or 1), even if formData.paid is 0 (which is falsy)
        const paidValue = (formData.paid === 1 || formData.paid === '1') ? 1 : 0;
        submitData.append('paid', paidValue);
        
        submitData.append('etc_2', formData.etc_2 || '');
        
        // Send selected video categories as array
        if (formData.selectedVideoCategories.length > 0) {
            formData.selectedVideoCategories.forEach((catId, index) => {
                submitData.append(`video_categories[${index}]`, catId);
            });
        }
        
        if (formData.thumb) {
            submitData.append('thumb', formData.thumb);
        }

        try {
            let response;
            if (editingVideo) {
                // Use POST with _method for updates (Laravel method spoofing)
                submitData.append('_method', 'PUT');
                response = await http.post(`/stream_videos/${editingVideo.id}`, submitData);
            } else {
                response = await http.post('/stream_videos', submitData);
            }

            if (response.data.success) {
                toast.success(editingVideo ? "Video updated successfully!" : "Video created successfully!");
                setShowModal(false);
                setFormData({
                    category_id: '',
                    desc: '',
                    video_link: '',
                    paid: 0,
                    etc_2: '',
                    selectedVideoCategories: [],
                    thumb: null
                });
                setEditingVideo(null);
                fetchVideos();
            } else {
                toast.error(response.data.message || "Failed to save video");
            }
        } catch (error) {
            console.error("Error saving video:", error);
            const errorMessage = error.response?.data?.message || error.message || "Error saving video";
            toast.error(errorMessage);
            if (error.response?.data?.errors) {
                console.error("Validation errors:", error.response.data.errors);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (video) => {
        setEditingVideo(video);
        // Get paid value: default to 0 (No) if not set
        const paidValue = video.paid !== null && video.paid !== undefined ? video.paid : 0;
        
        // Parse video categories from etc_3 (stored as JSON)
        let selectedCategories = [];
        if (video.etc_3) {
            try {
                selectedCategories = JSON.parse(video.etc_3);
                if (!Array.isArray(selectedCategories)) {
                    selectedCategories = [];
                }
            } catch (e) {
                console.error("Error parsing video categories:", e);
                selectedCategories = [];
            }
        }
        
        setFormData({
            category_id: video.category_id || '',
            desc: video.desc || '',
            video_link: video.video_link || '',
            paid: paidValue,
            etc_2: video.etc_2 || '',
            selectedVideoCategories: selectedCategories,
            thumb: null
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this video?")) {
            try {
                const response = await http.delete(`/stream_videos/${id}`);
                if (response.data.success) {
                    toast.success("Video deleted successfully!");
                    fetchVideos();
                }
            } catch (error) {
                console.error("Error deleting video:", error);
                toast.error("Error deleting video");
            }
        }
    };

    const resetForm = () => {
        setFormData({
            category_id: '',
            desc: '',
            video_link: '',
            paid: 0,
            etc_2: '',
            selectedVideoCategories: [],
            thumb: null
        });
        setEditingVideo(null);
        setShowModal(false);
    };

    const getVideoCategoryNames = (etc3Value) => {
        if (!etc3Value) return '-';
        try {
            const categoryIds = JSON.parse(etc3Value);
            if (!Array.isArray(categoryIds) || categoryIds.length === 0) return '-';
            
            const names = categoryIds
                .map(id => {
                    const category = videoCategories.find(cat => cat.id === parseInt(id));
                    return category ? category.name : null;
                })
                .filter(name => name !== null);
            
            return names.length > 0 ? names.join(', ') : '-';
        } catch (e) {
            return '-';
        }
    };

    const getCategoryName = (categoryId) => {
        const category = categories.find(cat => cat.id === categoryId);
        return category ? category.name : 'Unknown';
    };

    return (
        <div className="container-fluid">
            <Toaster />
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h3 className="card-title">Stream Videos</h3>
                            <button 
                                className="btn btn-primary"
                                onClick={() => setShowModal(true)}
                            >
                                + Add Video
                            </button>
                        </div>
                        <div className="card-body">
                            {isLoading ? (
                                <TaskSkeleton />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Category</th>
                                                <th>Description</th>
                                                <th>Video Link</th>
                                                <th>Thumbnail</th>
                                                <th>Paid</th>
                                                <th>Video Categories</th>
                                                <th>Created At</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {videos.map((video) => (
                                                <tr key={video.id}>
                                                    <td>{video.id}</td>
                                                    <td>{getCategoryName(video.category_id)}</td>
                                                    <td>{video.desc}</td>
                                                    <td>
                                                        <a 
                                                            href={video.video_link} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-primary"
                                                        >
                                                            View Video
                                                        </a>
                                                    </td>
                                                    <td>
                                                        {video.thumb ? (
                                                            <img 
                                                                src={`http://3.6.125.26/api/api/stream_videos/${video.id}/image`}
                                                                alt="Thumbnail"
                                                                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                                className="rounded"
                                                            />
                                                        ) : (
                                                            <span className="text-muted">No Image</span>
                                                        )}
                                                    </td>
                                                    <td>{video.paid === 1 ? 'Yes' : 'No'}</td>
                                                    <td>{getVideoCategoryNames(video.etc_3)}</td>
                                                    <td>{moment(video.created_at).format('DD/MM/YYYY HH:mm')}</td>
                                                    <td>
                                                        <button 
                                                            className="btn btn-sm btn-warning me-2"
                                                            onClick={() => handleEdit(video)}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleDelete(video.id)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingVideo ? 'Edit Video' : 'Add New Video'}
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={resetForm}
                                ></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">Category *</label>
                                                <select
                                                    className="form-control"
                                                    name="category_id"
                                                    value={formData.category_id}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Category</option>
                                                    {categories.map(category => (
                                                        <option key={category.id} value={category.id}>
                                                            {category.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">Video Link *</label>
                                                <input
                                                    type="url"
                                                    className="form-control"
                                                    name="video_link"
                                                    value={formData.video_link}
                                                    onChange={handleInputChange}
                                                    placeholder="https://example.com/video.mp4"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Description *</label>
                                        <textarea
                                            className="form-control"
                                            name="desc"
                                            value={formData.desc}
                                            onChange={handleInputChange}
                                            rows="3"
                                            required
                                        ></textarea>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Paid</label>
                                        <select
                                            className="form-control"
                                            name="paid"
                                            value={String(formData.paid)}
                                            onChange={handleInputChange}
                                        >
                                            <option value="0">No</option>
                                            <option value="1">Yes</option>
                                        </select>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label">Video Categories</label>
                                        <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                            {videoCategories.length > 0 ? (
                                                videoCategories.map((category) => (
                                                    <div key={category.id} className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id={`vidcat-${category.id}`}
                                                            checked={formData.selectedVideoCategories.includes(category.id)}
                                                            onChange={() => handleVideoCategoryChange(category.id)}
                                                        />
                                                        <label className="form-check-label" htmlFor={`vidcat-${category.id}`}>
                                                            {category.name}
                                                        </label>
                                                    </div>
                                                ))
                                            ) : (
                                                <p className="text-muted mb-0">No video categories available</p>
                                            )}
                                        </div>
                                        <small className="form-text text-muted">
                                            Select one or more video categories
                                        </small>
                                    </div>
                                    
                                    {/* Hidden field for etc_2 */}
                                    <input
                                        type="hidden"
                                        name="etc_2"
                                        value={formData.etc_2}
                                        onChange={handleInputChange}
                                    />
                                    <div className="mb-3">
                                        <label className="form-label">Thumbnail Image</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                        />
                                        <small className="form-text text-muted">
                                            Supported formats: JPG, PNG, GIF (Max 5MB)
                                        </small>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={resetForm}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Saving...' : (editingVideo ? 'Update' : 'Create')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
